/**
 * Enhanced AdminHomePage using the new component system
 * Demonstrates dashboard layout with stats cards and overview table
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  FiUsers,
  FiBookOpen,
  FiSearch,
  FiTool,
  FiUserCheck,
  FiArrowRight,
} from 'react-icons/fi';

// New component system imports
import {
  AdminLayout,
  Breadcrumb,
  DataTable,
  TableColumn,
} from '@grcs/components';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  Stack,
  useColorModeValue,
  VStack,
  HStack,
  Text,
  Badge,
  SimpleGrid,
} from '@chakra-ui/react';
import { AdminSummary } from './shared';
import { SEO } from '@grcs/components/SEO';

// Existing hooks and types
import {
  useSemesterOperations,
  useClassOperations,
  useUserOperations,
  useHomeworkOperations,
} from '@grcs/hook/useDatabase';
import { ClassProfile, HomeworkStudentProfile } from '@grcs/types/DataTypes';
import { UserRole } from '@grcs/types/UserRole';

interface DashboardStats {
  totalClasses: number;
  totalStudents: number;
  totalTeachers: number;
  activeHomework: number;
}

interface ClassOverviewData extends ClassProfile {
  teacher_name?: string;
  completion_rate?: number;
}

export const AdminHomePageEnhanced: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const surface = useColorModeValue('white', 'gray.800');
  const border = useColorModeValue('gray.200', 'gray.600');
  const muted = useColorModeValue('gray.600', 'gray.300');
  const hover = useColorModeValue('blue.50', 'gray.700');
  const semesterOps = useSemesterOperations();
  const classOps = useClassOperations();
  const userOps = useUserOperations();
  const homeworkOps = useHomeworkOperations();

  const [currentYear, setCurrentYear] = useState('');
  const [classes, setClasses] = useState<ClassOverviewData[]>([]);
  const [stats, setStats] = useState<DashboardStats>({
    totalClasses: 0,
    totalStudents: 0,
    totalTeachers: 0,
    activeHomework: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const sortedClasses = useMemo(() => {
    const parseSortKey = (name: string) => {
      const matches = name.match(/\d+/g) || [];
      const grade = matches[0] ? parseInt(matches[0], 10) : 0;
      const room = matches[1] ? parseInt(matches[1], 10) : 0;
      return { grade, room };
    };

    return [...classes].sort((a, b) => {
      const aKey = parseSortKey(a.class_name);
      const bKey = parseSortKey(b.class_name);

      if (aKey.grade !== bKey.grade) {
        return aKey.grade - bKey.grade;
      }

      if (aKey.room !== bKey.room) {
        return aKey.room - bKey.room;
      }

      return (a.class_name || '').localeCompare(b.class_name || '');
    });
  }, [classes]);
  // Fetch current semester year with comprehensive error handling
  useEffect(() => {
    const fetchCurrentYear = async () => {
      try {
        console.log('🗓️ Loading current semester for Admin dashboard...');
        const { data, error: semesterError } =
          await semesterOps.getCurrentSemesterYear();

        if (semesterError) {
          console.error('❌ Error fetching current semester:', semesterError);
          setError(
            'Failed to load semester information. Please try refreshing the page.',
          );
          return;
        }

        if (data && data.length > 0) {
          const semester = data[0].semester_year;
          console.log(`✅ Active semester found: ${semester}`);
          setCurrentYear(semester);
          setError(null); // Clear any previous errors
        } else {
          console.warn('⚠️ No active semester found');
          setError(
            'No active semester is currently set. Please go to Admin → Semester Management to create and activate a semester before viewing dashboard data.',
          );
          // Stop loading dashboard data if no active semester
          setIsLoading(false);
          return;
        }
      } catch (err) {
        console.error('💥 Unexpected error fetching current year:', err);
        setError(
          'An unexpected error occurred while loading semester information. Please try refreshing the page or contact support.',
        );
        setIsLoading(false);
      }
    };

    if (!currentYear) {
      fetchCurrentYear();
    }
  }, [currentYear, semesterOps]);

  // Fetch dashboard data - only when we have a valid semester and no errors
  useEffect(() => {
    const fetchDashboardData = async () => {
      // Don't load data if there's no current year or if there are semester errors
      if (!currentYear) return;
      if (error) {
        console.log(
          '⏭️ Skipping dashboard data loading - no active semester or error present',
        );
        // Reset stats to empty state
        setStats({
          totalClasses: 0,
          totalStudents: 0,
          totalTeachers: 0,
          activeHomework: 0,
        });
        setClasses([]);
        setIsLoading(false);
        return;
      }

      console.log(`📊 Loading dashboard data for semester: ${currentYear}`);
      setIsLoading(true);
      try {
        // Fetch classes for current semester
        const { data: classData } = await classOps.getClassesBySemester(
          currentYear,
        );
        const currentClasses = classData || [];

        // Fetch teacher count
        const { data: teacherData } = await userOps.getUsersByRole(
          UserRole.TEACHER,
        );
        const teacherCount = teacherData?.length || 0;

        // Calculate stats
        const totalStudents = currentClasses.reduce(
          (sum, c) => sum + (c.number_of_students || 0),
          0,
        );
        const totalClasses = currentClasses.length;

        // Fetch teacher data for classes
        const teacherTokens = [
          ...new Set(
            currentClasses.map((c) => c.teacher_token).filter(Boolean),
          ),
        ];
        const teacherMap = new Map<string, string>();

        if (teacherTokens.length > 0) {
          try {
            // Fetch teachers by tokens
            const { data: teachers } = await userOps.getUsersByRole(
              UserRole.TEACHER,
            );
            if (teachers) {
              teachers.forEach((teacher) => {
                if (teacherTokens.includes(teacher.user_token)) {
                  teacherMap.set(
                    teacher.user_token,
                    `${teacher.first_name} ${teacher.last_name}`,
                  );
                }
              });
            }
          } catch (err) {
            console.error('Error fetching teacher data:', err);
          }
        }

        // Fetch all homeworks for all classes to calculate real metrics
        const allClassTokens = currentClasses.map((c) => c.class_token);
        let totalActiveHomework = 0;
        const classCompletionRates = new Map<string, number>();

        // Calculate per-class completion rates and active homework count
        for (const classToken of allClassTokens) {
          try {
            // Get all homework for this class
            const { data: classHomeworks } =
              await homeworkOps.getHomeworkByClassId(classToken);
            const activeHomeworks = (classHomeworks || []).filter(
              (hw: any) => !hw.deleted,
            );
            totalActiveHomework += activeHomeworks.length;

            // Get homework-student relations to calculate completion rate
            const { data: submissions } =
              await homeworkOps.getAllHomeworksByClassId(classToken);
            if (submissions && submissions.length > 0) {
              const completedCount = (
                submissions as HomeworkStudentProfile[]
              ).filter((s) => s.completed).length;
              const completionRate = Math.round(
                (completedCount / submissions.length) * 100,
              );
              classCompletionRates.set(classToken, completionRate);
            } else {
              classCompletionRates.set(classToken, 0);
            }
          } catch (err) {
            console.error(`Error fetching data for class ${classToken}:`, err);
            classCompletionRates.set(classToken, 0);
          }
        }

        // Enhanced class data with teacher names and real completion rates
        const enhancedClasses: ClassOverviewData[] = currentClasses.map(
          (classItem) => ({
            ...classItem,
            teacher_name:
              teacherMap.get(classItem.teacher_token) || 'Not Assigned',
            completion_rate:
              classCompletionRates.get(classItem.class_token) || 0,
          }),
        );

        setClasses(enhancedClasses);
        setStats({
          totalClasses,
          totalStudents,
          totalTeachers: teacherCount,
          activeHomework: totalActiveHomework,
        });
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, [currentYear, error, classOps, userOps, homeworkOps]);

  // Event handlers
  const handleClassClick = (classItem: ClassOverviewData) => {
    navigate(`/admin/${currentYear}/class/${classItem.class_token}/students`);
  };

  // Table columns definition
  const columns: TableColumn<ClassOverviewData>[] = [
    {
      key: 'class_name',
      header: 'Class Name',
      sortable: true,
      render: (classItem: ClassOverviewData, value: string) => (
        <div>
          <div style={{ fontWeight: 600 }}>{value}</div>
          <div style={{ fontSize: '0.875rem', opacity: 0.8 }}>
            {classItem.semester_year}
          </div>
        </div>
      ),
    },
    {
      key: 'teacher_name',
      header: 'Teacher',
      render: (classItem: ClassOverviewData, value: string) =>
        value || 'Not Assigned',
    },
    {
      key: 'number_of_students',
      header: 'Students',
      sortable: true,
      render: (classItem: ClassOverviewData, value: number) => (
        <HStack spacing={2}>
          <FiUsers size={16} />
          <span>{value || 0}</span>
        </HStack>
      ),
    },
    {
      key: 'completion_rate',
      header: 'Progress',
      render: (classItem: ClassOverviewData, value: number) => (
        <HStack spacing={2}>
          <Badge
            colorScheme={value >= 80 ? 'green' : value >= 60 ? 'yellow' : 'red'}
            variant="subtle"
          >
            {value}%
          </Badge>
        </HStack>
      ),
    },
  ];

  const visibleClasses = sortedClasses.filter((item) =>
    `${item.class_name} ${item.teacher_name || ''}`
      .toLowerCase()
      .includes(search.trim().toLowerCase()),
  );
  return (
    <AdminLayout padding={3}>
      <SEO
        title="Admin Dashboard"
        description="Manage classes, students, teachers, and homework at Grand River Chinese School."
      />
      <VStack spacing={4} align="stretch">
        <Box>
          <Breadcrumb items={[{ label: 'Admin home', isCurrent: true }]} />
          <Flex
            justify="space-between"
            align="center"
            gap={3}
            wrap="wrap"
            mt={3}
          >
            <Box>
              <Heading size="md">School overview</Heading>
              <Text fontSize="sm" color={muted} mt={1}>
                Manage your school and follow class progress.
              </Text>
            </Box>
            <Button
              as={RouterLink}
              to="/admin/semesters"
              size="sm"
              variant="outline"
            >
              {currentYear ? `Semester ${currentYear}` : 'Manage semesters'}
            </Button>
          </Flex>
        </Box>
        <SimpleGrid columns={{ base: 2, md: 3, lg: 5 }} spacing={3}>
          {[
            { label: 'Classes', href: '/admin/class', icon: FiBookOpen },
            { label: 'Library', href: '/admin/library', icon: FiBookOpen },
            { label: 'Students', href: '/admin/student', icon: FiUsers },
            { label: 'Teachers', href: '/admin/teacher', icon: FiUserCheck },
            { label: 'Tools', href: '/admin/tools', icon: FiTool },
          ].map(({ label, href, icon: Icon }) => (
            <Box
              as={RouterLink}
              to={href}
              key={label}
              bg={surface}
              borderWidth="1px"
              borderColor={border}
              borderRadius="lg"
              p={3}
              _hover={{ bg: hover, borderColor: 'blue.400' }}
              _focusVisible={{
                outline: '2px solid',
                outlineColor: 'blue.400',
                outlineOffset: '2px',
              }}
            >
              <HStack justify="space-between">
                <HStack>
                  <Icon aria-hidden="true" />
                  <Text fontSize="sm" fontWeight="600">
                    {label}
                  </Text>
                </HStack>
                <FiArrowRight aria-hidden="true" />
              </HStack>
            </Box>
          ))}
        </SimpleGrid>
        {error ? (
          <Alert status="error" borderRadius="lg">
            <AlertIcon />
            <Text fontSize="sm">{error}</Text>
          </Alert>
        ) : (
          <>
            <AdminSummary
              loading={isLoading}
              items={[
                { label: 'Current classes', value: stats.totalClasses },
                { label: 'Enrolled students', value: stats.totalStudents },
                { label: 'Teachers', value: stats.totalTeachers },
                { label: 'Active assignments', value: stats.activeHomework },
              ]}
            />
            <Box
              bg={surface}
              borderWidth="1px"
              borderColor={border}
              borderRadius="lg"
              p={{ base: 3, md: 4 }}
              minW={0}
            >
              <Stack
                direction={{ base: 'column', md: 'row' }}
                justify="space-between"
                spacing={3}
                mb={3}
              >
                <Box>
                  <Heading size="sm">Class overview</Heading>
                  <Text fontSize="sm" color={muted} mt={1}>
                    Current semester enrollment and assignment completion.
                  </Text>
                </Box>
                <InputGroup size="sm" maxW={{ md: '300px' }}>
                  <InputLeftElement pointerEvents="none">
                    <FiSearch />
                  </InputLeftElement>
                  <Input
                    aria-label="Search classes"
                    placeholder="Search class or teacher"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    borderRadius="md"
                  />
                </InputGroup>
              </Stack>
              <Text fontSize="xs" color={muted} mb={3} role="status">
                {isLoading
                  ? 'Loading classes…'
                  : `${visibleClasses.length} of ${classes.length} classes`}
              </Text>
              <DataTable
                compact
                size="sm"
                striped={false}
                data={visibleClasses}
                columns={columns}
                loading={isLoading}
                clickableRows
                onRowClick={handleClassClick}
                actions={[
                  {
                    label: 'View students',
                    icon: <FiUsers />,
                    onClick: handleClassClick,
                    variant: 'secondary',
                  },
                ]}
                emptyState={
                  <VStack spacing={2} py={8} color={muted}>
                    <FiBookOpen size={28} />
                    <Text fontWeight="600">
                      {search.trim()
                        ? 'No matching classes'
                        : 'No classes this semester'}
                    </Text>
                    <Text fontSize="sm">
                      {search.trim()
                        ? 'Try another class or teacher name.'
                        : 'Create a class to begin organizing students.'}
                    </Text>
                    {search ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setSearch('')}
                      >
                        Clear search
                      </Button>
                    ) : (
                      <Button
                        as={RouterLink}
                        to="/admin/class/new"
                        size="sm"
                        colorScheme="blue"
                      >
                        Add class
                      </Button>
                    )}
                  </VStack>
                }
              />
            </Box>
          </>
        )}
      </VStack>
    </AdminLayout>
  );
};
